import { Skeleton } from '@noor/ui';
import type { ReactNode } from 'react';
import type {
  ChangeRequest,
  ModerationEntity,
  ModerationEntityType,
  Translate,
} from '@/entities/moderation';
import { CategorySnapshot } from './category-snapshot';
import { ProductSnapshot } from './product-snapshot';
import { PromotionSnapshot } from './promotion-snapshot';
import { RestaurantSnapshot } from './restaurant-snapshot';

interface EntitySnapshotProps {
  entityType: ModerationEntityType;
  entity: ModerationEntity;
  request: ChangeRequest;
  /** The review card is still loading — show the content skeleton. */
  loading: boolean;
  t: Translate;
  verdictInfo?: ReactNode;
  weekSchedule?: unknown;
}

/** One entity, rendered by its queue's single-column body. */
export function EntitySnapshot({
  entityType,
  entity,
  request,
  loading,
  t,
  verdictInfo,
  weekSchedule,
}: EntitySnapshotProps) {
  if (loading) return <SnapshotSkeleton />;
  switch (entityType) {
    case 'products':
      return (
        <ProductSnapshot
          entity={entity}
          request={request}
          t={t}
          verdictInfo={verdictInfo}
        />
      );
    case 'restaurants':
      return (
        <RestaurantSnapshot
          weekSchedule={weekSchedule}
          entity={entity}
          request={request}
          t={t}
          verdictInfo={verdictInfo}
        />
      );
    case 'categories':
      return (
        <CategorySnapshot
          entity={entity}
          request={request}
          t={t}
          verdictInfo={verdictInfo}
        />
      );
    case 'promotions':
      return (
        <PromotionSnapshot
          entity={entity}
          request={request}
          t={t}
          verdictInfo={verdictInfo}
        />
      );
  }
}

export function SnapshotSkeleton() {
  return (
    <div className="flex flex-col gap-4" data-testid="moderation-snapshot-skeleton">
      <Skeleton className="h-32 w-full rounded-[20px]" />
      <Skeleton className="h-24 w-full rounded-[20px]" />
      <Skeleton className="h-24 w-full rounded-[20px]" />
    </div>
  );
}
